import React, { createContext, useContext, useEffect, useState } from "react";
import { useMsal, MsalProvider } from "@azure/msal-react";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { msalConfig } from "./authConfig";

// Initialize MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Auth context type definition
type UserDetails = {
  username: string;
  name?: string;
  localAccountId?: string;
};

type AuthContextType = {
  isAuthenticated: boolean;
  userDetails: UserDetails | null;
  logout: () => void;
};

// Create the auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth context provider component
export const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { instance, accounts } = useMsal();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);

  useEffect(() => {
    const updateAuthState = async () => {
      if (accounts.length > 0) {
        setIsAuthenticated(true);
        setUserDetails({
          username: accounts[0].username,
          name: accounts[0].name,
          localAccountId: accounts[0].localAccountId,
        });
      } else {
        setIsAuthenticated(false);
        setUserDetails(null);
      }
    };

    updateAuthState();

    // Set up event listeners for login/logout events
    const callbackId = instance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
      ) {
        updateAuthState();
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [accounts, instance]);

  // Logout function
  const logout = () => {
    instance.logoutRedirect();
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, userDetails, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthContextProvider");
  }
  return context;
};

// Combined MSAL and Auth context provider
export const MSALAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthContextProvider>{children}</AuthContextProvider>
    </MsalProvider>
  );
};
